import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApolloClient, useMutation } from '@apollo/client';
import {
  Card, Form, Row, Input, Col, Switch, Button, Space, message, Upload,
} from 'antd';
import { ArrowLeftOutlined, UploadOutlined } from '@ant-design/icons';
import Editor from '../../../components/editor';
import { CREATE_TOPIC, GET_SUBJECT } from '../../../graphql';
import { uploadFiles } from '../../../upload';
import './styles.less';

const { Item: FormItem } = Form;
const NewTopic = () => {
  const { search } = useLocation();
  const navigate = useNavigate();
  const [createTopic, { loading }] = useMutation(CREATE_TOPIC);
  const client = useApolloClient();
  const [uploadLoading, setUploadLoading] = useState(false);

  const subjectId = new URLSearchParams(search).get('sub');
  const [content, setContent] = useState('');
  const chosenFiles = (attachment) => (attachment?.fileList ?? [])
    .map((file) => file.originFileObj || file);

  const handleFormSubmit = async (inputs) => {
    const { attachment, status, ...fields } = inputs;

    try {
      // `topicNo` is allocated server-side from a per-subject counter, so it
      // is no longer something the form supplies.
      const { data } = await createTopic({
        variables: {
          input: {
            ...fields,
            subjectId,
            content,
            status: status ? 'PUBLISHED' : 'DRAFT',
          },
        },
        update: (proxy, { data: { createTopic: created } }) => {
          const variables = { id: subjectId };
          const cached = proxy.readQuery({ query: GET_SUBJECT, variables });
          if (!cached) return;
          proxy.writeQuery({
            query: GET_SUBJECT,
            variables,
            data: { ...cached, topics: [...cached.topics, created] },
          });
        },
      });

      // Attachments go up after the topic exists, because a topic attachment
      // is addressed as subjectId/topicId.
      const files = chosenFiles(attachment);
      if (files.length > 0) {
        setUploadLoading(true);
        try {
          await uploadFiles({
            client,
            files,
            target: 'TOPIC',
            targetId: `${subjectId}/${data.createTopic.id}`,
          });
        } finally {
          setUploadLoading(false);
        }
      }

      message.success('Topic created successfully');
      navigate(-1);
    } catch (err) {
      message.error(err.message || 'Could not create topic');
    }
  };

  return (
    <div>
      <Card className="gx-card gx-p-1 new-topic">
        <Form requiredMark="optional" onFinish={handleFormSubmit} layout="vertical">
          <Row className="no-flex-column">
            <Col span={24} className="gx-mb-3">
              <div className="heading">
                <h3>New Topic</h3>
                <Button onClick={() => navigate(-1)} size="middle" shape="circle" icon={<ArrowLeftOutlined />} />
              </div>
            </Col>
            <Col span={24}>
              <FormItem valuePropName="checked" name="status" initialValue={false} required={false}>
                <Switch checkedChildren="Publish" unCheckedChildren="Draft" />
              </FormItem>
            </Col>
            <Col span={24}>
              <FormItem name="title" required label="Title">
                <Input placeholder="enter a title" />
              </FormItem>
            </Col>
            <Col xm={24} md={14}>
              <FormItem name="video" required={false} label="Video  Id">
                <Input placeholder="e.g ZZUyshYtsw" />
              </FormItem>
            </Col>
          </Row>
          <Col className="gx-mt-2" span={24}>
            <Editor content={content} setContent={setContent} />
          </Col>
          <Col className="gx-mt-4" span={24}>
            <FormItem
              label="Attachment"
              valuePropName="file"
              name="attachment"
            >
              <Upload
                listType="picture"
                beforeUpload={() => false}
              >
                <Button icon={<UploadOutlined />}>Attach files</Button>
              </Upload>
            </FormItem>
          </Col>
          <Col className="gx-mt-4" span={24}>
            <Space>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
              <Button loading={loading || uploadLoading} htmlType="submit" type="primary">Save Topic</Button>
            </Space>
          </Col>
        </Form>
      </Card>
    </div>
  );
};

export default NewTopic;
